import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { DollarSignIcon, PlusIcon, UsersIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import type { AccountDTO } from '@invenco/common-interface/accounts';
import type { ShippingPriceBandDTO, ShippingPriceDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages, formatDate, formatMoney, truncateNumber } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

import { sortBands } from '../sortBands';

const columnHelper = createColumnHelper<ShippingPriceDTO>();

/**
 * Formats price bands into a summary string showing markup percentages.
 * Example: "10% at $0.00, 15% at $10.00, 20% at $20.00, ..."
 */
function formatPriceBands(priceBands: ShippingPriceBandDTO[]): string {
  const formatted = sortBands(priceBands)
    .slice(0, 4)
    .map(({ markup }, index, sorted) => {
      const startingAt = index > 0 ? sorted[index - 1].maxCost : undefined;
      const markupPercent = `${truncateNumber(markup * 100, 1)}%`;
      if (startingAt === 0 || startingAt) {
        return `${markupPercent} at ${formatMoney(startingAt)}`;
      }
      return markupPercent;
    });

  return formatted.join(', ') + (priceBands.length > 4 ? ', ...' : '');
}

function getColumns(accountsById: Record<string, AccountDTO>) {
  return [
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('accountId', {
      header: 'Account',
      cell: ({ getValue }) => accountsById[getValue()]?.name ?? '',
    }),
    columnHelper.accessor('locationName', { header: 'Location' }),
    columnHelper.accessor('validFrom', {
      header: 'Valid from',
      cell: ({ getValue }) => formatDate(getValue()),
    }),
    columnHelper.accessor('validTo', {
      header: 'Until',
      cell: ({ getValue }) => formatDate(getValue()),
    }),
    columnHelper.accessor('priceBands', {
      header: 'Price bands',
      cell: ({ getValue }) => formatPriceBands(getValue()),
    }),
  ];
}

export function ShippingPriceList() {
  const { accountsGateway, shippingGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<ShippingPriceDTO>('/shipping/prices');

  // Fetch all accounts for lookup and filter
  const { data: accountsData } = useQuery({
    queryKey: ['accounts'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getAccounts({ cursor, take }, { signal }),
      ),
  });

  const accountsById = useMemo<Record<string, AccountDTO>>(
    () =>
      Object.fromEntries(
        accountsData?.flatMap((account) => (account.id ? [[account.id, account] as const] : [])) ??
          [],
      ),
    [accountsData],
  );

  const columns = useMemo(() => getColumns(accountsById), [accountsById]);

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
    filterDefs: useMemo(
      () => ({
        accountId: {
          variant: 'multiselect',
          label: 'Account',
          icon: UsersIcon,
          options: accountsData?.map((account) => account.id) ?? [],
          optionLabel: (accountId: string) => accountsById[accountId]?.name ?? accountId,
        },
      }),
      [accountsById, accountsData],
    ),
  });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'shippingPrices',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getShippingPrices({ search: input.search, cursor, take }, { signal }),
      ),
    placeholderData: keepPreviousData,
  });

  // Apply client-side account filter
  const filteredData = useMemo(() => {
    if (!data) return data;

    const accountFilter = filters.filterState.find((f) => f.id === 'accountId');
    const accountIds = accountFilter?.value as string[] | undefined;

    if (!accountIds || accountIds.length === 0) {
      return data;
    }

    return data.filter((price) => accountIds.includes(price.accountId));
  }, [data, filters.filterState]);

  const table = useDataTable({
    data: filteredData,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <DollarSignIcon />
        </HeaderIcon>
        <HeaderTitle>Shipping prices</HeaderTitle>
        <HeaderActions>
          <Button asChild>
            <Link to="/shipping/prices/new">
              <PlusIcon />
              Create
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
